import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
    classnames,
    CustomMarkdownEditor,
    FileUpload,
    FilterDropDown,
    LoadingIndicator,
    Thumbnail,
    Toast,
} from '@ygtechnologies/bygr-frontend-component-library';
import { ValidatingTextArea } from '@/components/forms/validation';
import { Input } from '@/components/generic-controls';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { getFileThumbnail, MeetingType, postFile, updateLetterSetupData } from '@/services/booking';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { FileData } from '@ygtechnologies/rubus-frontend-utilities';
import { toggleValue } from '@ygtechnologies/rubus-frontend-utilities';
import { LetterSetupData } from '../../MeetingOverview';
import theme from './MeetingLetterSetup.module.scss';
import { Button, ButtonBar, Card, Checkbox, CheckboxGroup, Dropzone, SlideToggle } from '@ygtechnologies/rubus-ui';

const acceptImages = { 'image/*': [] };

export interface CheckboxbDefinition {
    id: LetterChecklistId;
    caption(): ReactNode;
    templateText: string;
}

export enum LetterChecklistId {
    None = 'None',
    Owner = 'Owner',
    SentDate = 'SentDate',
    Unit = 'Unit',
    Floor = 'Floor',
    MeetingTime = 'MeetingTime',
    MeetingDate = 'MeetingDate',
}

interface MeetingSetupProps {
    letterSetup?: LetterSetupData;
    allMeetingsTypes: MeetingType[];
    onSaved(): void;
}

export const LetterChecklist: CheckboxbDefinition[] = [
    {
        id: LetterChecklistId.Owner,
        caption: () => <Trans>Owner</Trans>,
        templateText: 'Ola Nordmann',
    },
    {
        id: LetterChecklistId.SentDate,
        caption: () => <Trans>Sent date</Trans>,
        templateText: '2024-02-1309:34:44.2496780+00:00',
    },
    {
        id: LetterChecklistId.Unit,
        caption: () => <Trans>Unit</Trans>,
        templateText: 'H101',
    },
    {
        id: LetterChecklistId.Floor,
        caption: () => <Trans>Floor</Trans>,
        templateText: '1',
    },
    {
        id: LetterChecklistId.MeetingDate,
        caption: () => <Trans>Meeting date</Trans>,
        templateText: '2024-02-1309:34:44.2496780+00:00',
    },
    {
        id: LetterChecklistId.MeetingTime,
        caption: () => <Trans>Meeting time</Trans>,
        templateText: '12:00-13:00',
    },
];

const MeetingLetterSetup: React.FC<MeetingSetupProps> = ({ letterSetup, allMeetingsTypes, onSaved }) => {
    const [localSetupData, setLocalSetup] = useState<LetterSetupData>();
    const [isLogoLoading, setIsLogoLoading] = useState<boolean>(false);
    const [isSupportLogoLoading, setIsSupportLogoLoading] = useState<boolean>(false);
    const [isCardLoading, setIsCardLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (letterSetup) {
            if (letterSetup.connectedMeetings === null) {
                letterSetup.connectedMeetings = [];
            }

            if (letterSetup.optionalLetterSetupFields === null) {
                letterSetup.optionalLetterSetupFields = [];
            }

            setLocalSetup(letterSetup);
        }
    }, [letterSetup]);

    const cardTextLength = useMemo(() => {
        const length = localSetupData?.cardText ? localSetupData.cardText.length : 0;
        return length;
    }, [localSetupData]);

    const handleChangeValues = (changes: Partial<LetterSetupData>) => {
        setLocalSetup((oldValue) => ({ ...oldValue, ...changes }));
    };

    const handleOptionFieldChange = (id: LetterChecklistId, enabled: boolean) => {
        setLocalSetup((oldValue) => {
            return {
                ...oldValue,
                optionalLetterSetupFields: toggleValue(oldValue?.optionalLetterSetupFields || [], id, enabled),
            };
        });
    };

    const handleCardImageDrop = async (files) => {
        setIsCardLoading(true);
        const file = files[0];
        if (!file) {
            return;
        }
        var fileData = new FileData(file);
        await fileData.getDataPromise();
        if (file !== null) {
            const fileId = await postFile(fileData).then((x) => {
                setIsCardLoading(false);
                return x;
            });
            handleChangeValues({ cardImageToken: fileId });
        }
    };

    const handleLogoDrop = async (files) => {
        setIsLogoLoading(true);
        const file = files[0];
        if (!file) {
            return;
        }
        var fileData = new FileData(file);
        await fileData.getDataPromise();
        if (file !== null) {
            const fileId = await postFile(fileData).then((x) => {
                setIsLogoLoading(false);
                return x;
            });
            handleChangeValues({ logoImageToken: fileId });
        }
    };

    const handleSupportLogoDrop = async (files) => {
        setIsSupportLogoLoading(true);
        const file = files[0];
        if (!file) {
            return;
        }
        var fileData = new FileData(file);
        await fileData.getDataPromise();
        if (file !== null) {
            const fileId = await postFile(fileData).then((x) => {
                setIsSupportLogoLoading(false);
                return x;
            });
            handleChangeValues({ supportLogoImageToken: fileId });
        }
    };

    const handleSaveSetup = async () => {
        //Send backend change instead
        if (localSetupData && localSetupData.projectId && localSetupData.id) {
            if (!localSetupData.optionalLetterSetupFields) {
                localSetupData.optionalLetterSetupFields = [LetterChecklistId.None];
            }

            try {
                setSaving(true);
                await updateLetterSetupData(localSetupData.projectId, localSetupData.id, localSetupData);
                Toast.success(<Trans>Successfully updated setup</Trans>);
                triggerHandler(onSaved);
            } catch {
                Toast.error(<Trans>Something went wrong with saving</Trans>);
            } finally {
                setSaving(false);
            }
        }
    };

    return (
        <>
            {localSetupData && (
                <Card className={theme.letterContent}>
                    <div className={theme.letterHeaderText}>
                        <div className={theme.headerBar}>
                            <h1>
                                {localSetupData.meetingType?.name} <Trans>setup</Trans>
                            </h1>
                            <Button onClick={() => handleSaveSetup()} variant="primary" loading={saving}>
                                <Trans>Save</Trans>
                            </Button>
                        </div>
                        <div className={theme.subText}>
                            <Trans>Add key information, details, address and change the layout of the meeting cards.</Trans>
                        </div>
                    </div>
                    <div className={theme.letterRow}>
                        <div className={theme.rowHeader}>
                            <div className={theme.headerText}>
                                <Trans>Key information</Trans>
                            </div>
                            <div className={theme.subText}>
                                <Trans>Select what information to show on the letters.</Trans>
                            </div>
                        </div>
                        <div className={theme.rowContent}>
                            <CheckboxGroup className={theme.checkboxGrid} aria-label={t`Key information fields`}>
                                {LetterChecklist.map((x) => (
                                    <Checkbox
                                        inputSize="md"
                                        name={x.id}
                                        key={x.id}
                                        checked={localSetupData.optionalLetterSetupFields?.includes(x.id)}
                                        onChange={(e) => handleOptionFieldChange(x.id, e.target.checked)}>
                                        {x.caption()}
                                    </Checkbox>
                                ))}
                            </CheckboxGroup>
                        </div>
                    </div>

                    <div className={theme.letterRow}>
                        <div className={theme.rowHeader}>
                            <div className={classnames(theme.headerText)}>
                                <Trans>Connect additional meeting times</Trans>
                            </div>
                            <div className={theme.subText}>
                                <Trans>
                                    Connect one or more letters with the current. <br />
                                    This will include info from the connected meetings and make the meeting visible for the
                                    buyer.{' '}
                                </Trans>
                            </div>
                        </div>
                        <div className={theme.rowContent}>
                            <FilterDropDown
                                placeholder={t`Select connected meetings`}
                                label={<Trans>Connected meetings</Trans>}
                                data={allMeetingsTypes
                                    .filter((x) => x.id !== localSetupData.meetingType?.id)
                                    .map((meetingType) => ({
                                        id: meetingType.id,
                                        name: <>{meetingType.name}</>,
                                    }))}
                                value={localSetupData.connectedMeetings || []}
                                onChange={(v) => handleChangeValues({ connectedMeetings: v.map((x) => x.id) })}
                            />
                        </div>
                    </div>
                    <div className={theme.letterRow}>
                        <div className={theme.rowHeader}>
                            <div className={classnames(theme.headerText)}>
                                <Trans>Senderlogo and information</Trans>
                            </div>
                            <div className={theme.subText}>
                                <Trans>Select what logo to show in the letter.</Trans>
                                <br />
                                <Trans>You can also select a extra logo.</Trans>
                            </div>
                        </div>
                        <div className={classnames(theme.rowContent, theme.imageSpacer)}>
                            <Dropzone
                                label={<Trans>Main logo</Trans>}
                                accept={acceptImages}
                                loading={isLogoLoading}
                                onUpload={handleLogoDrop}
                                onRemove={() => handleChangeValues({ logoImageToken: undefined })}>
                                {localSetupData.logoImageToken && (
                                    <Thumbnail
                                        src={getFileThumbnail(localSetupData.logoImageToken)}
                                        className={theme.image}
                                    />
                                )}
                            </Dropzone>
                            <Dropzone
                                label={<Trans>Extra logo</Trans>}
                                accept={acceptImages}
                                loading={isSupportLogoLoading}
                                onUpload={handleSupportLogoDrop}
                                onRemove={() => handleChangeValues({ supportLogoImageToken: undefined })}>
                                {localSetupData.supportLogoImageToken && (
                                    <Thumbnail
                                        src={getFileThumbnail(localSetupData.supportLogoImageToken)}
                                        className={theme.image}
                                    />
                                )}
                            </Dropzone>
                        </div>
                    </div>
                    <div className={theme.letterRow}>
                        <div className={theme.rowHeader}>
                            <div className={classnames(theme.headerText)}>
                                <Trans>Sender information</Trans>
                            </div>
                            <div className={theme.subText}>
                                <Trans>Add address, postal number, phone number and organiasation number.</Trans>
                            </div>
                            <div className={classnames(theme.centerVertically, theme.marginTop)}>
                                <SlideToggle
                                    onChange={(e) => handleChangeValues({ showSenderInfo: e.target.checked })}
                                    checked={localSetupData.showSenderInfo}
                                />
                                <span className={classnames(theme.headerText)}>
                                    <Trans>Show sender information</Trans>
                                </span>
                            </div>
                        </div>
                        <div className={theme.rowContent}>
                            <div
                                className={classnames(theme.notificationContainer, {
                                    [theme.display]: localSetupData.showSenderInfo,
                                    [theme.dontDisplay]: !localSetupData.showSenderInfo,
                                })}>
                                <div className={theme.notificationText}>
                                    <CustomMarkdownEditor
                                        markdown={localSetupData.senderFreeText || ''}
                                        onChange={(text) => handleChangeValues({ senderFreeText: text })}
                                        mdClassName={theme.strongFix}
                                    />
                                    {/*
                                        <Input
                                                    onChange={(e) => handleChangeValues({contactEmail: e.target.value})}
                                                    value={localSetupData.contactEmail}
                                                    placeholder={t`Link to page`}
                                                    className={classnames(theme.inputBorder, 'form-control')}
                                                />
                                        */}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={theme.letterRow}>
                        <div className={theme.rowHeader}>
                            <div className={classnames(theme.headerText)}>
                                <Trans>Notification</Trans>
                            </div>
                            <div className={theme.subText}>
                                <Trans>
                                    Select the channels to notify owners. System notifications will allways occur, but you
                                    can select more channel to notify in.
                                </Trans>
                            </div>
                        </div>
                        <div className={theme.rowContent}>
                            <div className={theme.notificationContainer}>
                                <Card className={theme.notificationBox}>
                                    <Trans>Email</Trans>
                                    <SlideToggle
                                        onChange={(e) => handleChangeValues({ sendEmail: e.target.checked })}
                                        checked={localSetupData.sendEmail}></SlideToggle>
                                </Card>

                                <Card className={theme.notificationBox}>
                                    <Trans>SMS</Trans>
                                    <SlideToggle
                                        onChange={(e) => handleChangeValues({ sendSms: e.target.checked })}
                                        checked={localSetupData.sendSms}></SlideToggle>
                                </Card>
                            </div>
                        </div>
                    </div>

                    <div className={theme.letterRow}>
                        <div className={theme.rowHeader}>
                            <div className={classnames(theme.headerText)}>
                                <Trans>Meeting cards (for buyers/owners)</Trans>
                            </div>
                            <div className={theme.subText}>
                                <Trans>
                                    Meeting cards will show in the system (Rubus) and the buyer must accept date and times
                                    there.
                                </Trans>
                            </div>

                            <div className={classnames(theme.centerVertically, theme.marginTop)}>
                                <SlideToggle
                                    onChange={(e) => handleChangeValues({ showCardImage: e.target.checked })}
                                    checked={localSetupData.showCardImage}
                                />
                                <span className={classnames(theme.headerText)}>
                                    <Trans>Include an image</Trans>
                                </span>
                            </div>
                        </div>
                        <div className={theme.rowContent}>
                            <div className={theme.notificationContainer}>
                                <Input
                                    value={localSetupData.cardHeader || ''}
                                    placeholder={t`Title of the meeting card`}
                                    onChange={(e) => handleChangeValues({ cardHeader: e.target.value })}
                                    className={classnames('form-control')}
                                />
                                <ValidatingTextArea
                                    className="form-control"
                                    placeholder={t`Short description of the meeting`}
                                    rows={4}
                                    value={localSetupData.cardText || ''}
                                    onChange={(e) => handleChangeValues({ cardText: e.target.value })}
                                />
                                {200 - cardTextLength} <Trans>signs left</Trans>
                                {/* <div className={theme.linksContainer}>
                                        <div className={classnames(theme.labelRow)}>
                                            <span className={theme.labelBox}>
                                                <Trans>Button</Trans>
                                            </span>
                                            <Input
                                                value={localSetupData.buttonText || ''}
                                                placeholder={t`Title of button`}
                                                onChange={(e) => handleChangeValues({ buttonText: e.target.value })}
                                                className={classnames(theme.inputBorder, 'form-control')}
                                            />
                                        </div>
                                        <div className={classnames(theme.labelRow)}>
                                            <span className={theme.labelBox}>
                                                <Trans>Attachment</Trans>
                                            </span>
                                            <Input
                                                value={localSetupData.attachmentText || ''}
                                                placeholder={t`Title of attachment`}
                                                onChange={(e) => handleChangeValues({ attachmentText: e.target.value })}
                                                className={classnames(theme.inputBorder, 'form-control')}
                                            />
                                        </div>
                                    </div> */}
                                <div
                                    className={classnames(theme.imageRow, {
                                        [theme.display]: localSetupData.showCardImage,
                                        [theme.dontDisplay]: !localSetupData.showCardImage,
                                    })}>
                                    {isCardLoading && <LoadingIndicator />}
                                    {localSetupData.cardImageToken && (
                                        <Thumbnail
                                            src={getFileThumbnail(localSetupData.cardImageToken)}
                                            className={theme.image}
                                        />
                                    )}
                                    <div className={theme.imageDroparea}>
                                        <FileUpload fileType="image" single onDrop={handleCardImageDrop} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button onClick={() => handleSaveSetup()} variant="primary" loading={saving}>
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </Card>
            )}
        </>
    );
};

export default MeetingLetterSetup;
